import './IceBreaker.scss';

import { AutoAwesomeRounded, ChevronRightRounded, CloseRounded, FavoriteRounded, ShuffleRounded } from '@mui/icons-material';
import { Dialog, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DirectInteractionActionCreator from '../../actions/DirectInteractionActionCreator';
import UserActionCreator from '../../actions/UserActionCreator';
import Config from '../../config';
import useUserAndToken from '../../core/useUserAndToken';
import { isError } from '../../models/core/error/IError';
import { MESSAGES_PATH } from '../../models/Paths';
import { IStrangerUser } from '../../models/user/IStrangerUser/IStrangerUser';
import ChatActionCreator from '../../services/Chat/actions/ChatActionCreator';
import ResourceService from '../../services/i18n';
import useTranslation from '../../services/i18n/core/useTranslation';
import { IBerndIcebreakerText } from '../../temp/models/IBerndIcebreakerText';
import { useConsumeCoinsHandler } from '../InsufficientCoinsDialog/useConsumeCoinsHandler';

const CARD_ACCENTS = ['pink', 'purple', 'orange'] as const;

export interface IIceBreakerModalProps {
    Username: string;
    Profilid: string;
    strangerUser?: IStrangerUser;
    open: boolean;
    onSend?(): void;
    onClose(): void;
    embedded?: boolean;
}

export const IceBreakerModal = (props: IIceBreakerModalProps) => {
    const { strangerUser, open, onClose, Username, Profilid, onSend, embedded } = props;
    const [iceBreakerTexts, setIceBreakerTexts] = useState<IBerndIcebreakerText[]>([]);
    const [textContent, setTextContent] = useState<IBerndIcebreakerText[]>([]);

    const { user, token } = useUserAndToken();
    const { ICEBREAKER_TITLE, ICEBREAKER_SUBTITLE, ICEBREAKER_SURPRISE, ICEBREAKER_SURPRISE_HINT } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const theme = useTheme();

    useEffect(() => {
        if (user && token) {
            void (async () => {
                const res = await (await DirectInteractionActionCreator.getIcebreakerTexts(Profilid, token, user)).json();

                if (!isError(res)) {
                    setIceBreakerTexts(res);
                    setTextContent(res.length > 3 ? res.slice(0, 3) : res);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSendIcebreaker = async (textId: number) => {
        if (user && token) {
            await DirectInteractionActionCreator.sendIcebreakerText(Profilid, textId, token, user);
            if (strangerUser) {
                await dispatch(ChatActionCreator.createDialog(Username, strangerUser));
                history.push(MESSAGES_PATH.replace(':id?', Profilid));
            }
            onClose();
            await dispatch(UserActionCreator.refreshUser());
            onSend && onSend();
        }
    };

    const handler = useConsumeCoinsHandler(handleSendIcebreaker, Config.ICEBREAKER_AMOUNT);

    const handleSurprise = () => {
        const pool = iceBreakerTexts.length ? iceBreakerTexts : textContent;
        if (!pool.length) {
            return;
        }

        const visibleCount = Math.min(3, pool.length);
        const currentIds = textContent.map((item) => item.Textid).join(',');

        const pickRandom = () => {
            const shuffled = [...pool].sort(() => Math.random() - 0.5);
            return shuffled.slice(0, visibleCount);
        };

        let next = pickRandom();
        // Avoid showing the exact same set again when there's enough variety to differ.
        if (pool.length > visibleCount) {
            let attempts = 0;
            while (next.map((item) => item.Textid).join(',') === currentIds && attempts < 5) {
                next = pickRandom();
                attempts += 1;
            }
        }

        setTextContent(next);
    };

    const title = ResourceService.replace(ICEBREAKER_TITLE, { name: Username });

    const cards = (
        <div className="icebreaker__list">
            {textContent?.map(({ Text, Textid }, index) => (
                <button
                    type="button"
                    key={String(Textid)}
                    className={`icebreaker__card icebreaker__card--${CARD_ACCENTS[index % CARD_ACCENTS.length]} ${theme.palette.mode}`}
                    onClick={() => handler(Textid)}
                >
                    <span className="icebreaker__card-accent" aria-hidden="true" />
                    <Typography className="icebreaker__card-text">{Text}</Typography>
                    <span className="icebreaker__chevron">
                        <ChevronRightRounded />
                    </span>
                </button>
            ))}
        </div>
    );

    const surprise = textContent.length ? (
        <button type="button" className="icebreaker__surprise" onClick={handleSurprise}>
            <span className="icebreaker__surprise-sparkle" aria-hidden="true">
                <AutoAwesomeRounded />
            </span>
            <span className="icebreaker__surprise-copy">
                <span className="icebreaker__surprise-title">{ICEBREAKER_SURPRISE}</span>
                <span className="icebreaker__surprise-hint">{ICEBREAKER_SURPRISE_HINT}</span>
            </span>
            <span className="icebreaker__surprise-shuffle" aria-hidden="true">
                <ShuffleRounded />
            </span>
        </button>
    ) : null;

    if (embedded) {
        return (
            <div className="icebreaker-embedded">
                <Typography className="icebreaker__title icebreaker__title--embedded">{title}</Typography>
                <Typography className="icebreaker__subtitle">{ICEBREAKER_SUBTITLE}</Typography>
                {cards}
                {surprise}
            </div>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: 'icebreaker__paper' }}
            BackdropProps={{ sx: { backgroundColor: 'rgba(28, 18, 38, 0.45)' } }}
        >
            <span className="icebreaker__badge" aria-hidden="true">
                <FavoriteRounded />
            </span>

            <button type="button" className="icebreaker__close" onClick={onClose} aria-label="Schließen">
                <CloseRounded />
            </button>

            <div className="icebreaker__body">
                <Typography className="icebreaker__title">{title}</Typography>
                <Typography className="icebreaker__subtitle">{ICEBREAKER_SUBTITLE}</Typography>
                {cards}
                {surprise}
            </div>
        </Dialog>
    );
};

export default IceBreakerModal;
